/*  MUI  */
import Check from '@mui/icons-material/Check';
import {
    Box,
    Button,
    Grid,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    Typography
} from '@mui/material';

/*  Shared  */
import { AuvikASMLogo } from 'application/Shared/Components/App/AuvikLogo';
import { FontSizes } from 'application/Shared/Constants';
import { PartnerView } from 'application/Shared/Views';

/*  React  */
import React from 'react';
import { withTranslation } from 'react-i18next';

const Title = withTranslation()(({ t }) => (
    <>
        <Typography
            variant="body2"
            color="viewPricing.textTitle"
            style={{
                fontSize: FontSizes[5],
                marginTop: '16px'
            }}
        >
            {t('Trial.viewPricing.chooseYourPlan')}
        </Typography>
        <Typography
            variant="body2"
            color="viewPricing.textSubtitle"
            style={{
                fontSize: FontSizes[4],
                marginTop: '16px'
            }}
        >
            {t('Trial.viewPricing.title.subscribe')}
        </Typography>
    </>
));

const HorizontalBar = ({ color }) => (
    <Box my="32px" borderBottom="8px solid" borderColor={color} />
);

const ContactUsButton = withTranslation()(
    ({ t, planType, 'data-trackid': dataTrackid }) => {
        const subject =
            planType === 'exclusive'
                ? t('Trial.viewPricing.exclusivePlan.email.subject')
                : t('Trial.viewPricing.planPrice.email.subject');

        const body =
            planType === 'exclusive'
                ? t('Trial.viewPricing.exclusivePlan.email.body')
                : t('Trial.viewPricing.planPrice.email.body');

        return (
            <a
                href={`mailto:sales@auvik.com?subject=${encodeURIComponent(
                    subject
                )}&body=${encodeURIComponent(body)}`}
            >
                <Button
                    variant="contained"
                    data-trackid={`${dataTrackid}Button`}
                    size="anmSmall"
                    sx={{
                        boxShadow: 'none',
                        minWidth: '200px'
                    }}
                >
                    {t('Trial.viewPricing.contactTeam')}
                </Button>
            </a>
        );
    }
);

const ChoosePlanPerUser = withTranslation()(({ t }) => (
    <Box my="32px" display="flex" alignItems="center">
        <Box width="50%" display="flex" alignItems="center">
            {/* Commenting out $3 per user version */}
            {/* <Box width="40%" display="flex" justifyContent="flex-end">
                <Typography
                    variant="body2"
                    color="viewPricing.textPerUser"
                    style={{
                        fontSize: '70px',
                        fontWeight: 600
                    }}
                >
                    {t('Trial.viewPricing.planPrice.1')}
                </Typography>
            </Box>
            <Box width="60%" textAlign="left" ml="8px">
                <Typography
                    variant="body2"
                    color="viewPricing.textPerUser"
                    style={{
                        textTransform: 'uppercase'
                    }}
                >
                    {t('Trial.viewPricing.planPrice.2')}
                </Typography>
                <Typography
                    variant="body2"
                    color="viewPricing.textPerUser"
                    style={{
                        fontSize: FontSizes[4],
                        fontWeight: 600
                    }}
                >
                    {t('Trial.viewPricing.planPrice.3')}
                </Typography>
            </Box> */}
            <Box textAlign="left" ml="24px">
                <Typography
                    variant="body2"
                    color="viewPricing.textPerUser"
                    style={{
                        fontSize: FontSizes[4],
                        fontWeight: 600
                    }}
                >
                    {t('Trial.viewPricing.planPrice.featuresIncluded')}
                </Typography>
            </Box>
        </Box>
        <Box width="50%">
            <ContactUsButton
                planType="perEndUser"
                data-trackid="viewPricingChoosePlan"
            />
        </Box>
    </Box>
));

const FeatureList = withTranslation()(({ t, features }) => (
    <List dense>
        {features.map((feature) => (
            <ListItem
                disablePadding
                alignItems="flex-start"
                key={`feature_item_${feature}`}
            >
                <ListItemIcon
                    sx={{
                        marginTop: '4px',
                        minWidth: '32px'
                    }}
                >
                    <Check sx={{ color: 'viewPricing.icon' }} />
                </ListItemIcon>
                <ListItemText
                    primary={t(`Trial.viewPricing.features.${feature}`)}
                />
            </ListItem>
        ))}
    </List>
));

const PlanPerUserFeatures = () => {
    const featuresColumnOne = [
        'appsMonitored',
        'unlimitedDevices',
        'unlimitedReports',
        'logs'
    ];
    const featuresColumnTwo = [
        'detailedReports',
        'automated',
        'sharedMonitoring',
        'healthScore'
    ];

    return (
        <Grid container my="32px">
            <Grid item xs={6}>
                <FeatureList features={featuresColumnOne} />
            </Grid>
            <Grid item xs={6}>
                <FeatureList features={featuresColumnTwo} />
            </Grid>
        </Grid>
    );
};

const ChooseExclusivePlan = withTranslation()(({ t }) => (
    <Box my="32px" display="flex" alignItems="center">
        <Box width="50%" textAlign="left" ml="24px">
            <Typography
                variant="body2"
                color="viewPricing.textExclusive"
                style={{
                    fontSize: FontSizes[4],
                    fontWeight: 600
                }}
            >
                {t('Trial.viewPricing.exclusive')}
            </Typography>
            <Typography
                variant="body2"
                color="viewPricing.textExclusive"
                style={{
                    fontSize: FontSizes[3],
                    fontWeight: 600
                }}
            >
                {t('Trial.viewPricing.mspPartnerPricing')}
            </Typography>
        </Box>
        <Box width="50%">
            <ContactUsButton
                planType="exclusive"
                data-trackid="viewPricingExclusiveMspPartnerPricing"
            />
        </Box>
    </Box>
));

const Pricing = () => (
    <Paper
        elevation={0}
        variant="outlined"
        style={{ margin: '0 auto', maxWidth: '928px', textAlign: 'center' }}
        mx="auto"
    >
        <Box px={['32px', '32px', '64px', '64px', '128px']} py="32px">
            <AuvikASMLogo style={{ width: '100px' }} />
            <Title />
            <HorizontalBar color="viewPricing.dividerPerUser" />
            <ChoosePlanPerUser />
            <PlanPerUserFeatures />
            <HorizontalBar color="viewPricing.dividerExclusive" />
            <ChooseExclusivePlan />
        </Box>
    </Paper>
);

export const ViewPricing = ({ trialExpired = false }) => {
    // For expired trials, we only want to show the pricing page without the sidebar.
    if (trialExpired) {
        return (
            <Box my="64px">
                <Pricing />
            </Box>
        );
    }

    return (
        <PartnerView>
            <Pricing />
        </PartnerView>
    );
};
